import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  MessageSquare, Search, Filter, Eye, Mail, 
  RefreshCw, ChevronDown, X, AlertCircle, Clock, CheckCircle
} from 'lucide-react';

const SubmissionsTab = ({ authAxios }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterService, setFilterService] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const serviceLabels = {
    'ACCOMMODATION': 'Accommodation Solutions',
    'HMO': 'HMO & Shared Living',
    'LANDLORD': 'Property & Landlord Solutions',
    'PROPERTY_REQ': 'Property Requirement',
    'LANDLORD_SOL': 'Landlord Solution',
    'R2R': 'Rent-to-Rent',
    'R2SA': 'Serviced Accommodation (SA)',
    'R2HMO': 'HMO Operations',
    'DEAL_SOURCING': 'Deal Sourcing',
    'LEASE': 'Lease Options',
    'OTHER': 'Other'
  };

  useEffect(() => {
    fetchSubmissions();
  }, [authAxios]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get('/contact');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = 
      sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterService === 'all' || sub.service === filterService;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'new': return AlertCircle;
      case 'pending': return Clock;
      case 'resolved': return CheckCircle;
      default: return Clock;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="submissions-tab">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search by name, email, or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="submissions-search"
            className="w-full bg-[#111111] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none transition-colors"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <select
            value={filterService}
            onChange={(e) => setFilterService(e.target.value)}
            data-testid="submissions-filter"
            className="bg-[#111111] border border-white/10 pl-10 pr-8 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none appearance-none cursor-pointer min-w-[180px]"
          >
            <option value="all">All Services</option>
            <option value="ACCOMMODATION">Accommodation Solutions</option>
            <option value="HMO">HMO & Shared Living</option>
            <option value="LANDLORD">Property & Landlord Solutions</option>
            <option value="PROPERTY_REQ">Property Requirement</option>
            <option value="LANDLORD_SOL">Landlord Solution</option>
            <option value="OTHER">Other</option>
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
        </div>
        <button
          onClick={fetchSubmissions}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors rounded"
          data-testid="submissions-refresh"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="font-mono text-sm">Refresh</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
          <p className="text-white/50 mt-4 font-mono text-sm">Loading...</p>
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="text-center py-12 bg-[#111111] border border-white/5">
          <MessageSquare className="w-12 h-12 text-white/20 mx-auto" />
          <p className="text-white/50 mt-4 font-mono">No submissions found</p>
        </div>
      ) : (
        <div className="bg-[#111111] border border-white/5 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-4 text-left font-mono text-xs text-white/50 uppercase">Name</th>
                <th className="px-6 py-4 text-left font-mono text-xs text-white/50 uppercase">Email</th>
                <th className="px-6 py-4 text-left font-mono text-xs text-white/50 uppercase">Service</th>
                <th className="px-6 py-4 text-left font-mono text-xs text-white/50 uppercase">Status</th>
                <th className="px-6 py-4 text-left font-mono text-xs text-white/50 uppercase">Date</th>
                <th className="px-6 py-4 text-right font-mono text-xs text-white/50 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredSubmissions.map(sub => {
                const StatusIcon = getStatusIcon(sub.status);
                return (
                  <tr key={sub.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 font-mono text-sm">{sub.name}</td>
                    <td className="px-6 py-4">
                      <a href={`mailto:${sub.email}`} className="text-white/70 hover:text-[#D4AF37] text-sm">{sub.email}</a>
                    </td>
                    <td className="px-6 py-4 text-white/50 text-sm">{serviceLabels[sub.service] || sub.service}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono ${getStatusColor(sub.status)}`}>
                        <StatusIcon className="w-3 h-3" />{sub.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-white/50 text-sm">{formatDate(sub.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => setSelectedSubmission(sub)} className="p-2 text-white/50 hover:text-[#D4AF37]" data-testid={`view-submission-${sub.id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-[#111111] border border-white/10 w-full max-w-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h3 className="font-mono font-bold">Submission Details</h3>
              <button onClick={() => setSelectedSubmission(null)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Name</label>
                <p className="font-mono">{selectedSubmission.name}</p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Email</label>
                <a href={`mailto:${selectedSubmission.email}`} className="font-mono text-[#D4AF37] hover:underline">
                  {selectedSubmission.email}
                </a>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Service</label>
                <p className="font-mono">{serviceLabels[selectedSubmission.service] || selectedSubmission.service}</p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Message</label>
                <p className="text-white/80 text-sm leading-relaxed bg-[#0A0A0A] p-4 border border-white/5">
                  {selectedSubmission.message}
                </p>
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-1">Submitted</label>
                <p className="font-mono text-sm">{formatDate(selectedSubmission.created_at)}</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-white/5 flex gap-3">
              <a
                href={`mailto:${selectedSubmission.email}`}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Reply via Email
              </a>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="px-4 py-2 border border-white/20 text-white/70 font-mono text-sm hover:bg-white/5 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SubmissionsTab;
